import { useCallback, useRef, useState, useMemo } from 'react';
import SvgEditor from '../utils/svgEditor';
import { openWindow } from '@umu/umu-dom';
import { IUseSvgEditorParams, IUseSvgEditorReturn, INodeConfig } from '../interface/svgEditor';
import { getCertificateTemplate } from '../dataFlow/svgEditor';
import { downloadCertificatePackage } from '../utils/download';
import { IPackageDownloadResult } from '../interface/download';
import { IWatermarkConfig } from '../interface';
import { onTransformCertificateDate } from '../utils';
const dateFormatMap: Record<string, string> = {
    cn: 'MM 月 DD, YYYY',
    en: 'MMM DD, YYYY',
    jp: 'YYYY.MM.DD',
};

// SVG 编辑器 Hook
export const useSvgEditor = ({
    svgContainerRef,
    nodeConfigs = [],
    onLinkClick,
    currentSystem = 'cn',
}: IUseSvgEditorParams): IUseSvgEditorReturn => {
    const svgEditorRef = useRef<any>(null);
    const [nodeList, setNodeList] = useState<INodeConfig[]>(nodeConfigs);
    const nodeValuesRef = useRef<Record<string, string>>({});
    // 水印相关状态
    const [watermarkEnabled, setWatermarkEnabled] = useState<boolean>(false);
    const [watermarkConfigString, setWatermarkConfigString] = useState<string>(
        '{"text":"example watermark","color":"#4A93FF","size":6,"trasparency":0.2,"rotate":30,"rows":6,"splitCount":4}',
    );
    const dateFormat = useMemo(() => {
        return dateFormatMap?.[currentSystem] || 'MM 月 DD, YYYY';
    }, [currentSystem]);
    const updateNodeList = useCallback((newNodeList: INodeConfig[]) => {
        setNodeList(newNodeList);
    }, []);
    const updateNodeConfig = useCallback((key: string, info: Partial<INodeConfig>) => {
        setNodeList((prevList) => {
            return prevList.map((config) => {
                if (config.key === key) {
                    return { ...config, ...info };
                }
                return config;
            });
        });
    }, []);
    // 链接点击处理函数
    const handleLinkClick = useCallback(
        (e: Event) => {
            e.preventDefault();
            e.stopPropagation();

            const target = e.target as SVGElement;
            const url = target.getAttribute('data-link');
            const linkType = target.getAttribute('data-link-type');

            if (!url) {
                return;
            }

            // 如果有自定义处理函数，先执行
            if (onLinkClick && linkType) {
                const shouldOpen = onLinkClick(url, linkType, target);
                if (!shouldOpen) {
                    return; // 业务逻辑阻止了跳转
                }
            }

            // 默认跳转逻辑
            if (url.startsWith('http://') || url.startsWith('https://')) {
                openWindow(url, '_blank');
            }
        },
        [onLinkClick],
    );

    // 绑定链接事件
    const bindLinkEvents = useCallback(() => {
        if (!svgContainerRef.current) {
            return;
        }

        // 移除旧的事件监听器
        const existingLinks = svgContainerRef.current.querySelectorAll('[data-link]');
        existingLinks.forEach((element) => {
            element.removeEventListener('click', handleLinkClick);
        });

        // 添加新的事件监听器
        const linkElements = svgContainerRef.current.querySelectorAll('[data-link]');
        linkElements.forEach((element) => {
            element.addEventListener('click', handleLinkClick);
        });
    }, [svgContainerRef, handleLinkClick]);

    // 渲染 SVG
    const renderSvg = useCallback(() => {
        if (!svgContainerRef.current || !svgEditorRef.current) {
            return;
        }

        try {
            const svgContent = svgEditorRef.current.getSvg();
            svgContainerRef.current.innerHTML = svgContent;

            // 渲染后重新绑定事件
            setTimeout(() => {
                bindLinkEvents();
            }, 0);
        } catch (_error) {}
    }, [svgContainerRef, bindLinkEvents]);

    // 使用自定义 SVG 文本初始化编辑器
    const loadCustomSvgTemplate = useCallback(
        (svgText: string): boolean => {
            if (!svgContainerRef.current) {
                return false;
            }

            try {
                // 创建 SVG 编辑器
                svgEditorRef.current = new SvgEditor(svgText);

                // 初始化节点值（使用最新的 nodeList）
                // 应用默认值
                const editor = svgEditorRef.current;
                if (editor) {
                    // 使用最新的 nodeList 获取默认值
                    const infoMap: Record<string, string> = nodeList.reduce((acc, config) => {
                        acc[config.key as string] = config?.defaultValue || '';
                        return acc;
                    }, {} as Record<string, string>);
                    const { link = '', qr = '', ...rest } = infoMap;
                    // 应用默认值到 SVG 编辑器
                    editor.setQr(qr);
                    editor.setLink(link);
                    editor.setBatchContent(rest || {});
                    // 确保 nodeValuesRef 也使用最新的值
                    renderSvg();
                }

                return true;
            } catch (_error) {
                return false;
            }
        },
        [svgContainerRef, renderSvg, nodeList],
    );

    // 初始化 SVG 编辑器
    const initializeSvgEditor = useCallback(
        async (system?: string): Promise<boolean> => {
            if (!svgContainerRef.current) {
                return false;
            }

            try {
                // 使用封装函数获取模板内容
                const svgText = await getCertificateTemplate(system);
                // 创建 SVG 编辑器
                svgEditorRef.current = new SvgEditor(svgText);

                // 初始化节点值（使用最新的 nodeList）

                // 应用默认值
                const editor = svgEditorRef.current;
                if (editor) {
                    // 使用最新的 nodeList 获取默认值
                    const infoMap: Record<string, string> = nodeList.reduce((acc, config) => {
                        acc[config.key as string] = config?.defaultValue || '';
                        return acc;
                    }, {} as Record<string, string>);
                    const { link = '', qr = '', date = '', ...rest } = infoMap;
                    // 应用默认值到 SVG 编辑器
                    editor.setQr(qr);
                    editor.setLink(link);
                    if (date) {
                        editor.setDate(
                            onTransformCertificateDate(
                                date,
                                !!system ? dateFormatMap?.[system] || dateFormat : dateFormat,
                            ),
                        );
                    }
                    editor.setBatchContent(rest || {});
                    // 渲染 SVG
                    renderSvg();
                }

                return true;
            } catch (_error) {
                // 尝试使用备用 SVG 内容
                try {
                    const fallbackSvg = `<svg width="800" height="600" xmlns="http://www.w3.org/2000/svg"></svg>`;
                    svgEditorRef.current = new SvgEditor(fallbackSvg);
                    renderSvg();
                    return true;
                } catch (_fallbackError) {
                    return false;
                }
            }
        },
        [svgContainerRef, dateFormat, renderSvg, nodeList],
    );
    const updateNodeValues = () => {
        const values = nodeValuesRef.current;
        Object.keys(values).forEach((key) => {
            applyNodeChange(key, values[key]);
        });
    };
    // 应用节点更改
    const applyNodeChange = useCallback(
        (key: string, value: string) => {
            if (!svgEditorRef.current) {
                return;
            }
            try {
                // 更新节点值
                // 根据节点类型调用相应的方法
                switch (key) {
                    case 'qr':
                        svgEditorRef.current.setQr(value);
                        break;
                    case 'link':
                        svgEditorRef.current.setLink(value);
                        break;
                    case 'date':
                        svgEditorRef.current.setDate(onTransformCertificateDate(value, dateFormat));
                        break;
                    default:
                        svgEditorRef.current.setBatchContent({
                            [key as string]: value,
                        });
                        break;
                }

                // 重新渲染 SVG
                renderSvg();
            } catch (_error) {}
        },
        [renderSvg, dateFormat],
    );

    // 重置所有更改
    const resetAllChanges = useCallback(async () => {
        try {
            // 重置节点值
            const resetValues: Record<string, string> = {};
            nodeList.forEach((config) => {
                resetValues[config.key] = config.defaultValue;
            });
            nodeValuesRef.current = resetValues;

            // 重新初始化
            await initializeSvgEditor();
        } catch (_error) {}
    }, [initializeSvgEditor]);

    // 处理输入框变化
    const handleInputChange = useCallback((key: string, value: string) => {
        nodeValuesRef.current[key] = value;
    }, []);

    // 处理按钮点击
    const handleButtonClick = useCallback(
        (key: string) => {
            const value = nodeValuesRef.current[key];
            if (value) {
                applyNodeChange(key, value);
            }
        },
        [applyNodeChange],
    );

    // 下载包含 SVG、PNG、JPEG 的压缩包
    const handleDownloadSvg = useCallback(async (): Promise<IPackageDownloadResult | null> => {
        // 参数校验
        if (!svgEditorRef.current || !svgContainerRef.current) {
            return null;
        }

        try {
            // 获取 SVG 内容
            const svgContent = svgEditorRef.current.getSvg();

            // 获取 SVG 元素（优先获取容器内的 SVG 元素）
            const svgElement =
                (svgContainerRef.current.querySelector('svg') as HTMLElement | null) || svgContainerRef.current;

            // 调用独立的下载函数
            const result = await downloadCertificatePackage({
                svgContent,
                svgElement,
            });
            return result;
        } catch (_error) {
            return null;
        }
    }, [svgContainerRef]);

    // 处理文件上传
    const handleFileUpload = useCallback(
        async (file: File | null, onError?: (message: string) => void, onSuccess?: () => void): Promise<boolean> => {
            if (!file) {
                return false;
            }

            // 验证文件类型
            if (!file.type.includes('svg') && !file.name.endsWith('.svg')) {
                onError?.('请上传 SVG 格式的文件');
                return false;
            }

            try {
                // 读取文件内容
                const fileContent = await file.text();

                // 验证是否为有效的 SVG
                const trimmedContent = fileContent.trim();
                if (!trimmedContent.startsWith('<svg') && !trimmedContent.startsWith('<?xml')) {
                    onError?.('文件格式不正确，请上传有效的 SVG 文件');
                    return false;
                }

                // 加载自定义 SVG 模板
                const success = loadCustomSvgTemplate(fileContent);
                if (success) {
                    onSuccess?.();
                    return true;
                } else {
                    onError?.('加载 SVG 模板失败，请检查文件格式');
                    return false;
                }
            } catch (error) {
                onError?.('读取文件失败，请重试');
                return false;
            }
        },
        [loadCustomSvgTemplate],
    );

    // 应用水印配置
    const applyWatermark = useCallback(() => {
        if (!svgEditorRef.current || !watermarkEnabled || !watermarkConfigString.trim()) {
            return;
        }

        try {
            // 将字符串转为 JSON
            const watermarkConfig: IWatermarkConfig = JSON.parse(watermarkConfigString.trim());

            // 调用 SvgEditor 的水印设置方法
            svgEditorRef.current.setWatermark(watermarkConfig);

            // 重新渲染 SVG
            renderSvg();
        } catch (error) {
            console.error('水印配置解析失败:', error);
            // 可以在这里添加错误提示
        }
    }, [watermarkEnabled, watermarkConfigString, renderSvg]);

    // 清除水印
    const clearWatermark = useCallback(() => {
        if (!svgEditorRef.current) {
            return;
        }

        try {
            // 调用 SvgEditor 的清除水印方法
            svgEditorRef.current.clearWatermark();

            // 重新渲染 SVG
            renderSvg();
        } catch (error) {
            console.error('清除水印失败:', error);
        }
    }, [renderSvg]);

    // 处理水印开关变化
    const handleWatermarkEnabledChange = useCallback(
        (enabled: boolean) => {
            setWatermarkEnabled(enabled);
            if (!enabled) {
                // 开关关闭时清除水印
                clearWatermark();
            }
        },
        [clearWatermark],
    );

    return {
        nodeValuesRef,
        nodeList,
        updateNodeConfig,
        updateNodeList,
        initializeSvgEditor,
        updateNodeValues,
        loadCustomSvgTemplate,
        handleFileUpload,
        handleInputChange,
        handleButtonClick,
        resetAllChanges,
        handleDownloadSvg,
        // 水印相关
        watermarkEnabled,
        watermarkConfigString,
        setWatermarkEnabled: handleWatermarkEnabledChange,
        setWatermarkConfigString,
        applyWatermark,
        clearWatermark,
    };
};
